import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useTranslation } from 'react-i18next';
import { clientStatsQueryKeys } from '../../../hooks/consultant/use-consultant-client-stats';
import { useNotification } from '../../../hooks/use-notification';
import {
  DocumentSignatureService,
  type ExpiryWarningDays,
  type IDocumentSignatureService,
} from '../../../services/document-signature';
import { getHttpErrorStatus, resolveApiErrorMessage } from '../../../utils/error.utils';
import { openPresignedUrlInNewTab } from '../../../utils/open-presigned-url-in-new-tab';
import { deadlinesQueryKeys } from '../deadlines/deadline-query-keys';
import { documentsArchiveQueryKeys } from '../query-keys';

export function useDeleteDocument() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: (documentId: string) => service.deleteDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deadlinesQueryKeys.all });
      showSuccess(t('deleteSuccess'));
    },
    onError: () => {
      showError(t('deleteError'));
    },
  });
}

export function useArchiveDocument() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: (documentId: string) => service.archiveDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deadlinesQueryKeys.all });
      showSuccess(t('archiveSuccess'));
    },
    onError: () => {
      showError(t('archiveError'));
    },
  });
}

export function useUnarchiveDocument() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: (documentId: string) => service.unarchiveDocument(documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deadlinesQueryKeys.all });
      showSuccess(t('unarchiveSuccess'));
    },
    onError: () => {
      showError(t('unarchiveError'));
    },
  });
}

/**
 * "Collega come sostituto" (T3.2): questo documento SIGNED prende il posto di
 * uno più vecchio della stessa categoria, che viene soft-archiviato. Oltre
 * all'archivio invalida anche il feed alert del consulente: la supersessione
 * può chiudere una card document_expiring/document_expired (motore slot T3.1).
 */
export function useSupersedeDocument() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: ({ documentId, supersededId }: { documentId: string; supersededId: string }) =>
      service.supersedeDocument(documentId, supersededId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deadlinesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: clientStatsQueryKeys.all });
      showSuccess(t('supersedeSuccess'));
    },
    onError: (err: unknown) => {
      if (getHttpErrorStatus(err) === 409) {
        // Ciclo (self/già sostituito), categoria diversa o replacement PENDING.
        showError(t('supersedeConflict'));
      } else {
        showError(resolveApiErrorMessage(err, t));
      }
    },
  });
}

export function useUpdateDocumentExpiry() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: ({
      documentId,
      expiresAt,
      expiryWarningDays,
      assigneeUserId,
    }: {
      documentId: string;
      expiresAt: Date | null;
      expiryWarningDays?: ExpiryWarningDays | null;
      assigneeUserId?: string | null;
    }) => service.updateExpiry(documentId, expiresAt, expiryWarningDays, assigneeUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentsArchiveQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: deadlinesQueryKeys.all });
      showSuccess(t('expiryUpdateSuccess'));
    },
    onError: () => {
      showError(t('expiryUpdateError'));
    },
  });
}

export function useDownloadDocument() {
  const service = useInjection<IDocumentSignatureService>(DocumentSignatureService);
  const { showError } = useNotification();
  const { t } = useTranslation('documents');

  return useMutation({
    mutationFn: (documentId: string) =>
      openPresignedUrlInNewTab(() => service.getDownloadUrl(documentId)),
    onError: () => {
      showError(t('errorDownload'));
    },
  });
}
